import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { CheckIcon, CircleAlertIcon, XIcon } from '@/components/atoms/icons';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useTranslation, useUIColors } from '@/core/hooks';
import { getResponsiveValue } from '@/core/utils/responsive';
import React from 'react';
import {
    Modal,
    Pressable,
    StyleSheet,
    View,
    useWindowDimensions,
} from 'react-native';
import type { ConfirmationDialogProps } from './ConfirmationDialog.types';

export type { ConfirmationDialogProps } from './ConfirmationDialog.types';

/**
 * Web confirmation dialog. Mirrors the native dialog (same alert icon and
 * Yes/No actions) with a mobile-first layout that scales up on wider viewports.
 * Reading window dimensions keeps the responsive values in sync while resizing.
 */
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    visible,
    title,
    message,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    type = 'phone',
}) => {
    const colors = useUIColors();
    const { t } = useTranslation();
    useWindowDimensions();

    const maxWidth = getResponsiveValue(340, 420, 460);
    const iconSize = getResponsiveValue(48, 56, 64);
    const cardPadding = getResponsiveValue(20, 24, 24);
    const overlayPadding = getResponsiveValue(16, 24, 24);

    const getDefaultMessage = () => {
        if (type === 'email') {
            return message || t('dialogs.editEmailConfirmation');
        }
        if (type === 'phone') {
            return message || t('dialogs.editPhoneConfirmation');
        }
        return message;
    };

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onCancel}
        >
            <View style={[styles.overlay, { paddingHorizontal: overlayPadding }]}>
                <Pressable
                    style={StyleSheet.absoluteFill}
                    onPress={onCancel}
                    accessibilityRole="button"
                    accessibilityLabel={cancelText || t('common.no')}
                />
                <View
                    style={[
                        styles.container,
                        {
                            backgroundColor: colors.backgroundLevel1,
                            maxWidth,
                            padding: cardPadding,
                        },
                    ]}
                >
                    <CircleAlertIcon size={iconSize} color={colors.fontPrimary} />
                    {title ? (
                        <Typography variant="title3" color="primary" style={styles.text}>
                            {title}
                        </Typography>
                    ) : null}
                    <Typography
                        variant={title ? 'body' : 'headline'}
                        color="primary"
                        style={styles.text}
                    >
                        {title ? message : getDefaultMessage()}
                    </Typography>
                    <View style={styles.buttonRow}>
                        <Button
                            title={cancelText || t('common.no')}
                            variant="outline"
                            onPress={onCancel}
                            leading={<XIcon color={colors.fontPrimary} />}
                            style={styles.button}
                        />
                        <Button
                            title={confirmText || t('common.yes')}
                            variant="primary"
                            onPress={onConfirm}
                            leading={<CheckIcon />}
                            style={styles.button}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: UI_PRIMITIVES.overlayBackdrop,
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: {
        width: '100%',
        borderRadius: 16,
        alignItems: 'center',
        gap: 16,
    },
    text: {
        textAlign: 'center',
        width: '100%',
    },
    buttonRow: {
        flexDirection: 'row',
        gap: 16,
        width: '100%',
        marginTop: 4,
    },
    button: {
        flex: 1,
        height: 48,
    },
});

export default ConfirmationDialog;
